<template>
  <div class="transaction-detail-container">
    <!-- Header -->
    <div class="page-header">
      <div>
        <h1>Transaction: {{ transaction ? transaction.reference : '' }}</h1>
        <span
          v-if="transaction"
          class="status-badge large"
          :class="transaction.status"
        >
          {{ transaction.status_display }}
        </span>
      </div>
      <div class="header-actions">
        <button
          v-if="transaction && transaction.status === 'confirmed'"
          class="btn btn-success"
          @click="markAsPaid"
        >
          <i class="fas fa-money-bill-wave" /> Marquer comme payé
        </button>
        <router-link
          to="/wallets/transactions"
          class="btn btn-secondary"
        >
          <i class="fas fa-arrow-left" /> Retour
        </router-link>
      </div>
    </div>

    <div
      v-if="transaction"
      class="transaction-content"
    >
      <!-- Info Cards -->
      <div class="info-grid">
        <div class="info-card">
          <div class="info-label">
            Fournisseur
          </div>
          <div class="info-value">
            {{ transaction.supplier_name }}
          </div>
        </div>

        <div class="info-card">
          <div class="info-label">
            Type
          </div>
          <div class="info-value">
            {{ transaction.type_display }}
          </div>
        </div>

        <div class="info-card">
          <div class="info-label">
            Date création
          </div>
          <div class="info-value">
            {{ formatDateTime(transaction.created_at) }}
          </div>
        </div>

        <div class="info-card highlight">
          <div class="info-label">
            Montant Net
          </div>
          <div class="info-value large">
            {{ formatAmount(transaction.amount) }} DT
          </div>
        </div>
      </div>

      <!-- Amount Breakdown -->
      <div class="section">
        <h3>
          <i class="fas fa-calculator" /> Détail des Montants
        </h3>
        <div class="amount-breakdown">
          <div class="breakdown-row">
            <span class="label">Prix TTC (Total commandes)</span>
            <span class="value">{{ formatAmount(transaction.price_ttc) }} DT</span>
          </div>
          <div class="breakdown-row">
            <span class="label">Prix HT</span>
            <span class="value">{{ formatAmount(transaction.price_ht) }} DT</span>
          </div>
          <div class="breakdown-row subtract">
            <span class="label">Frais de livraison (déduits)</span>
            <span class="value">- {{ formatAmount(transaction.delivery_fees) }} DT</span>
          </div>
          <div class="breakdown-row subtract">
            <span class="label">Frais société</span>
            <span class="value">- {{ formatAmount(transaction.company_fees) }} DT</span>
          </div>
          <div class="breakdown-row total">
            <span class="label">Montant Net Fournisseur</span>
            <span class="value">{{ formatAmount(transaction.amount) }} DT</span>
          </div>
        </div>
      </div>

      <!-- Status History -->
      <div
        v-if="transaction.confirmed_at || transaction.paid_at"
        class="section"
      >
        <h3>
          <i class="fas fa-history" /> Historique
        </h3>
        <div class="history-list">
          <div class="history-item">
            <div class="history-icon created">
              <i class="fas fa-plus" />
            </div>
            <div class="history-content">
              <div class="history-title">
                Créé (En Attente)
              </div>
              <div class="history-date">
                {{ formatDateTime(transaction.created_at) }}
              </div>
            </div>
          </div>

          <div
            v-if="transaction.confirmed_at"
            class="history-item"
          >
            <div class="history-icon confirmed">
              <i class="fas fa-check" />
            </div>
            <div class="history-content">
              <div class="history-title">
                Confirmé
                <span
                  v-if="transaction.confirmed_by_name"
                  class="history-by"
                >
                  par {{ transaction.confirmed_by_name }}
                </span>
              </div>
              <div class="history-date">
                {{ formatDateTime(transaction.confirmed_at) }}
              </div>
            </div>
          </div>

          <div
            v-if="transaction.paid_at"
            class="history-item"
          >
            <div class="history-icon paid">
              <i class="fas fa-money-bill-wave" />
            </div>
            <div class="history-content">
              <div class="history-title">
                Payé
                <span
                  v-if="transaction.paid_by_name"
                  class="history-by"
                >
                  par {{ transaction.paid_by_name }}
                </span>
              </div>
              <div class="history-date">
                {{ formatDateTime(transaction.paid_at) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Linked Payment/Invoice -->
      <div class="section">
        <h3>
          <i class="fas fa-link" /> Liens
        </h3>
        <div class="links-grid">
          <div
            v-if="transaction.payment_id"
            class="link-card"
          >
            <i class="fas fa-receipt" />
            <div>
              <div class="link-label">
                Paiement
              </div>
              <div class="link-value">
                #{{ transaction.payment_id }}
              </div>
            </div>
          </div>
          <div
            v-if="transaction.invoice_id"
            class="link-card"
          >
            <i class="fas fa-file-invoice" />
            <div>
              <div class="link-label">
                Facture
              </div>
              <div class="link-value">
                #{{ transaction.invoice_id }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Orders -->
      <div class="section">
        <h3>
          <i class="fas fa-boxes" /> Commandes ({{ transaction.orders_count }})
        </h3>
        <div
          v-if="transaction.orders && transaction.orders.length > 0"
          class="orders-list"
        >
          <table class="orders-table">
            <thead>
              <tr>
                <th>ID Commande</th>
                <th>Nom</th>
                <th>Statut</th>
                <th class="text-right">
                  Prix TTC
                </th>
                <th class="text-right">
                  Prix HT
                </th>
                <th class="text-right">
                  Frais Livraison
                </th>
              </tr>
            </thead>
            <tbody>
              <tr
                v-for="order in transaction.orders"
                :key="order.id"
              >
                <td>
                  <strong>{{ order.order_id }}</strong>
                </td>
                <td>{{ order.order_name }}</td>
                <td>{{ order.order_status }}</td>
                <td class="text-right">
                  {{ formatAmount(order.price_ttc) }} DT
                </td>
                <td class="text-right">
                  {{ formatAmount(order.price_ht) }} DT
                </td>
                <td class="text-right">
                  {{ formatAmount(order.delivery_fee) }} DT
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <div
          v-else
          class="empty-state"
        >
          <p>Détails des commandes non disponibles</p>
        </div>
      </div>

      <!-- Description -->
      <div
        v-if="transaction.description"
        class="section"
      >
        <h3>
          <i class="fas fa-comment" /> Description
        </h3>
        <p class="description-text">
          {{ transaction.description }}
        </p>
      </div>
    </div>

    <div
      v-else-if="loading"
      class="loading-state"
    >
      <i class="fas fa-spinner fa-spin" />
      <p>Chargement...</p>
    </div>
  </div>
</template>

<script>
import axios from 'axios'

export default {
  name: 'TransactionDetail',
  data() {
    return {
      transaction: null,
      loading: false,
    }
  },
  created() {
    this.loadTransaction()
  },
  methods: {
    async loadTransaction() {
      this.loading = true
      try {
        const response = await axios.get(`/api/wallets/wallet-transactions/${this.$route.params.id}/`)
        this.transaction = response.data
      } catch (error) {
        this.$toast.error('Erreur lors du chargement de la transaction')
      } finally {
        this.loading = false
      }
    },

    async markAsPaid() {
      const confirmed = await this.$bvModal.msgBoxConfirm('Marquer cette transaction comme payée ?', {
        title: 'Confirmation',
        okVariant: 'primary',
        okTitle: 'Confirmer',
        cancelTitle: 'Annuler',
      })
      if (!confirmed) return

      try {
        const response = await axios.post(`/api/wallets/wallet-transactions/${this.transaction.id}/mark-paid/`)
        this.transaction = response.data
        this.$toast.success('Transaction marquée comme payée')
      } catch (error) {
        this.$toast.error('Erreur lors du marquage')
      }
    },

    formatAmount(amount) {
      if (!amount) return '0.000'
      return parseFloat(amount).toFixed(3)
    },

    formatDateTime(date) {
      if (!date) return '-'
      return new Date(date).toLocaleDateString('fr-FR', {
        year: 'numeric',
        month: 'long',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
      })
    },
  },
}
</script>

<style lang="scss" scoped>
.transaction-detail-container {
  padding: 1.5rem;
  max-width: 1000px;
  margin: 0 auto;
}

.page-header {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 1.5rem;

  h1 {
    font-size: 1.75rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 0.5rem;
  }

  .header-actions {
    display: flex;
    gap: 0.75rem;
  }
}

.status-badge {
  display: inline-block;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-size: 0.875rem;
  font-weight: 500;

  &.large {
    padding: 0.5rem 1rem;
    font-size: 1rem;
  }

  &.pending {
    background: #fef3c7;
    color: #f59e0b;
  }

  &.confirmed {
    background: #d1fae5;
    color: #10b981;
  }

  &.paid {
    background: #ede9fe;
    color: #8b5cf6;
  }
}

.info-grid {
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
  gap: 1rem;
  margin-bottom: 1.5rem;
}

.info-card {
  background: white;
  border-radius: 12px;
  padding: 1rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  .info-label {
    font-size: 0.75rem;
    color: #64748b;
    margin-bottom: 0.25rem;
  }

  .info-value {
    font-size: 1rem;
    font-weight: 600;
    color: #1e293b;

    &.large {
      font-size: 1.5rem;
      font-family: 'Monaco', monospace;
    }
  }

  &.highlight {
    background: linear-gradient(135deg, #6366f1 0%, #8b5cf6 100%);
    color: white;

    .info-label { color: rgba(255,255,255,0.8); }
    .info-value { color: white; }
  }
}

.section {
  background: white;
  border-radius: 12px;
  padding: 1.5rem;
  margin-bottom: 1.5rem;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);

  h3 {
    font-size: 1rem;
    font-weight: 600;
    color: #1e293b;
    margin: 0 0 1rem;
    display: flex;
    align-items: center;
    gap: 0.5rem;
  }
}

.amount-breakdown {
  .breakdown-row {
    display: flex;
    justify-content: space-between;
    padding: 0.75rem 0;
    border-bottom: 1px solid #e2e8f0;

    .label {
      color: #64748b;
    }

    .value {
      font-family: 'Monaco', monospace;
      font-weight: 500;
    }

    &.subtract {
      .value { color: #ef4444; }
    }

    &.total {
      border-bottom: none;
      padding-top: 1rem;
      font-weight: 700;

      .label { color: #1e293b; }
      .value {
        font-size: 1.25rem;
        color: #6366f1;
      }
    }
  }
}

.history-list {
  display: flex;
  flex-direction: column;
  gap: 1rem;
}

.history-item {
  display: flex;
  align-items: flex-start;
  gap: 1rem;

  .history-icon {
    width: 36px;
    height: 36px;
    border-radius: 50%;
    display: flex;
    align-items: center;
    justify-content: center;

    &.created {
      background: #e0e7ff;
      color: #6366f1;
    }

    &.confirmed {
      background: #d1fae5;
      color: #10b981;
    }

    &.paid {
      background: #ede9fe;
      color: #8b5cf6;
    }
  }

  .history-content {
    .history-title {
      font-weight: 500;
      color: #1e293b;

      .history-by {
        font-weight: 400;
        color: #64748b;
      }
    }

    .history-date {
      font-size: 0.875rem;
      color: #64748b;
    }
  }
}

.links-grid {
  display: flex;
  gap: 1rem;

  .link-card {
    display: flex;
    align-items: center;
    gap: 0.75rem;
    padding: 1rem;
    background: #f8fafc;
    border-radius: 8px;

    i {
      font-size: 1.5rem;
      color: #6366f1;
    }

    .link-label {
      font-size: 0.75rem;
      color: #64748b;
    }

    .link-value {
      font-weight: 600;
      color: #1e293b;
    }
  }
}

.orders-table {
  width: 100%;
  border-collapse: collapse;

  th, td {
    padding: 0.75rem;
    text-align: left;
    border-bottom: 1px solid #e2e8f0;
  }

  th {
    background: #f8fafc;
    font-size: 0.75rem;
    font-weight: 600;
    color: #64748b;
    text-transform: uppercase;
  }

  .text-right {
    text-align: right;
  }
}

.description-text {
  white-space: pre-wrap;
  color: #64748b;
  line-height: 1.6;
}

.empty-state, .loading-state {
  text-align: center;
  padding: 2rem;
  color: #64748b;
}

.btn {
  padding: 0.5rem 1rem;
  border-radius: 6px;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  border: none;
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;

  &.btn-secondary {
    background: #f1f5f9;
    color: #64748b;
    &:hover { background: #e2e8f0; }
  }

  &.btn-success {
    background: #10b981;
    color: white;
    &:hover { background: #059669; }
  }
}
</style>
